import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Package, FileText, DollarSign, Clock, CheckCircle,
  LogOut, Menu, X, RefreshCw, TrendingUp, Bell
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProveedorDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('orders');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  // Mock data for supplier
  const [purchaseOrders, setPurchaseOrders] = useState([
    { id: 'PO-001', date: '2026-04-10', items: 15, total: 12500, status: 'pending' },
    { id: 'PO-002', date: '2026-04-08', items: 8, total: 7800, status: 'confirmed' },
    { id: 'PO-003', date: '2026-04-05', items: 22, total: 18900, status: 'delivered' },
    { id: 'PO-004', date: '2026-04-01', items: 12, total: 9400, status: 'paid' },
  ]);

  const stats = {
    pendingOrders: 2,
    totalRevenue: 48600,
    avgOrderValue: 12150,
    deliveredOrders: 8
  };

  const navItems = [
    { id: 'orders', label: 'Órdenes de Compra', icon: Package },
    { id: 'quotes', label: 'Cotizaciones', icon: FileText },
    { id: 'invoices', label: 'Facturas', icon: DollarSign },
    { id: 'history', label: 'Historial', icon: Clock },
  ];

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-700',
      'confirmed': 'bg-blue-100 text-blue-700',
      'delivered': 'bg-green-100 text-green-700',
      'paid': 'bg-emerald-100 text-emerald-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pendiente',
      'confirmed': 'Confirmado',
      'delivered': 'Entregado',
      'paid': 'Pagado'
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-teal-800 text-white transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-teal-700">
          <div className="flex items-center justify-between">
            {sidebarOpen && <img src="/logo.png" alt="Lunch & Roll" className="h-8" />}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-teal-700 rounded-lg">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === item.id 
                  ? 'bg-teal-600 text-white' 
                  : 'text-teal-200 hover:bg-teal-700'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {sidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-teal-700">
          {sidebarOpen && (
            <div className="mb-4">
              <p className="text-teal-200 text-sm">{user?.name}</p>
              <p className="text-teal-300 text-xs">{user?.company}</p>
            </div>
          )}
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2 text-teal-200 hover:bg-teal-700 rounded-lg">
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Salir</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-stone-800">Portal de Proveedor</h2>
            <p className="text-stone-500">{user?.company || 'Mi Empresa'}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" className="gap-2">
              <Bell className="w-4 h-4" />
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">3</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <p className="text-3xl font-bold text-stone-800">{stats.pendingOrders}</p>
            <p className="text-stone-500 text-sm">Órdenes Pendientes</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
              <DollarSign className="w-6 h-6 text-emerald-600" />
            </div>
            <p className="text-3xl font-bold text-stone-800">${stats.totalRevenue.toLocaleString()}</p>
            <p className="text-stone-500 text-sm">Facturación Total</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-stone-800">${stats.avgOrderValue.toLocaleString()}</p>
            <p className="text-stone-500 text-sm">Ticket Promedio</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-stone-800">{stats.deliveredOrders}</p>
            <p className="text-stone-500 text-sm">Entregas del Mes</p>
          </div>
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
            <div className="p-6 border-b border-stone-100">
              <h3 className="text-lg font-semibold text-stone-800">Órdenes de Compra</h3>
            </div>
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase">ID Orden</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase">Items</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-stone-500 uppercase">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {purchaseOrders.map(order => (
                  <tr key={order.id} className="hover:bg-stone-50">
                    <td className="px-6 py-4 font-mono font-medium text-stone-800">{order.id}</td>
                    <td className="px-6 py-4 text-stone-600">{order.date}</td>
                    <td className="px-6 py-4 text-stone-600">{order.items} productos</td>
                    <td className="px-6 py-4 font-semibold text-stone-800">${order.total.toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {order.status === 'pending' && (
                        <Button size="sm" className="bg-teal-600 hover:bg-teal-700">
                          Confirmar
                        </Button>
                      )}
                      {order.status === 'confirmed' && (
                        <Button size="sm" variant="outline">
                          Ver Detalle
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Other tabs placeholder */}
        {['quotes', 'invoices', 'history'].includes(activeTab) && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-12 text-center">
            <FileText className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-stone-700 mb-2">
              {navItems.find(n => n.id === activeTab)?.label}
            </h3>
            <p className="text-stone-500">Esta sección está en desarrollo</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ProveedorDashboard;
